import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import Tooltip from "@mui/material/Tooltip";
import { useMediaQuery, useTheme } from "@mui/material";

export default function ProjectGraph({ projectData, totalProjects, mode }) {
  const chartRef = useRef(null);
  const [tooltipData, setTooltipData] = useState(null); 
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 }); 
  const theme = useTheme();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const isLg_mScreen = useMediaQuery(theme.breakpoints.up("lg_m"));
  useEffect(() => {
    const chartInstance = echarts.init(chartRef.current);

    const sortedProjectData = projectData
      .map((project) => ({
        ...project,
        totalHours: project.hours + project.minutes / 60,
      }))
      .sort((a, b) => b.totalHours - a.totalHours)
      .reverse()
      .slice(0, 6);

    const createGradient = (color) => {
      const hexColor = color.startsWith("#")
        ? color.substring(0, 7)
        : "#000000";
        if (hexColor?.startsWith("#f")) {
          return {
            type: "linear",
            x: 0,
            y: 0,
            x2: 1,
            y2: 1,
            colorStops: [
              { offset: 0.5, color: "#F0F0F0B3" },
              { offset: 0.2, color: "#E0E0E080" },
              { offset: 1, color: "#D0D0D0" },
            ],
            global: false,
          };
        }
      
        if (hexColor?.startsWith("#1")) {
          return {
            type: "linear",
            x: 0,
            y: 0,
            x2: 1,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#4D4D4D" },
              { offset: 1, color: "#4D4D4D" },
            ],
            global: false,
          };
        }
      return {
        type: "linear",
        x: 0,
        y: 0,
        x2: 1,
        y2: 1,
        colorStops: [
          { offset: 0.5, color: `${hexColor}B3` },
          { offset: 0.2, color: `${hexColor}80` },
          { offset: 1, color: hexColor },
        ],
        global: false,
      };
    };

    const option = {
      dataset: {
        source: sortedProjectData.map((project) => [
          project.totalHours,
          project.name,
          project.hours,
          project.minutes,
          createGradient(project.color),
        ]),
      },
      grid: {
        containLabel: false,
        top: "5%",
        bottom: "1%",
        left: "0",
      },
      xAxis: { name: "Hours", show: false },
      yAxis: { type: "category", show: false },
      series: [
        {
          type: "bar",
          encode: {
            x: 0,
            y: 1,
          },
          itemStyle: {
            color: function (params) {
              return params.data[4];
            },
          },
          label: {
            show: true,
            position: "insideLeft",
            offset: [15, 0],
            formatter: function (params) {
              const projectName = params.value[1];
              let hours = params.value[2];
              let minutes = params.value[3];
              if (minutes === 60) {
                hours += 1;
                minutes = 0;
              }
              return `${projectName.length > 14 ? `${projectName.substring(0, 14)}...` : projectName} [${hours}h ${minutes}m]`;
            },
            fontWeight: "bold",
            color: mode === "light" ? "#000000" : "#FFFFFF",
          },
          barGap: "15px",
          barWidth: sortedProjectData.length === 1 ? "50%" : "80%",
        },
      ],
    };    

    chartInstance.setOption(option);

    chartInstance.on("mousemove", (e) => {
      const dataIndex = e.dataIndex;
      if (dataIndex !== undefined) {
        const project = sortedProjectData[dataIndex];
        if (project.name.length > 14) {
          const tooltipContent = `${project.name}`;
          setTooltipData({
            content: tooltipContent,
          });
          setTooltipPosition({
            x: e.event.offsetX + 100, 
            y: e.event.offsetY + 10, 
          });
        } else {
          setTooltipData(null); 
        }
      }
    });

    chartInstance.on("mouseout", () => {
      setTooltipData(null);
    });

    window.addEventListener("resize", () => {
      chartInstance.resize();
    });

    return () => {
      chartInstance.dispose();
      window.removeEventListener("resize", () => {
        chartInstance.resize();
      });
    };
  }, [projectData, mode]);

  const chartWidth = projectData.length === 1 ? "80%" : "100%";
  const chartHeight = projectData.length ===1 ? "200%": "300%";
  return (
    <div style={{ position: "relative" }}>
      <div
        ref={chartRef}
        style={{
          width: chartWidth,
          height: chartHeight,
          // top: "-10px",
          // left: "-35px",
        }}
      />
      {tooltipData && (
        <Tooltip
          title={tooltipData.content}
          open={!!tooltipData}
        >
          <span
            style={{
              position: "absolute",
              top: tooltipPosition.y,
              left: tooltipPosition.x,
              zIndex: 10,
              transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Tooltip>
      )}
    </div>
  );
}
